// Central content store — bilingual (TR/EN), persisted to localStorage.
// Components read state via useContent() and language via useLang().

import { useEffect, useState } from 'react';
import { api } from './api.js';

const CONTENT_KEY = 'glamm:site:v3';
const LANG_KEY = 'glamm:lang';

/* ======================= Language ======================= */

const LangStoreImpl = () => {
  let lang = (typeof localStorage !== 'undefined' && localStorage.getItem(LANG_KEY)) || 'tr';
  const subs = new Set();
  return {
    get: () => lang,
    set: (l) => {
      if (l !== 'tr' && l !== 'en') return;
      lang = l;
      try { localStorage.setItem(LANG_KEY, l); } catch {}
      subs.forEach(fn => fn(l));
    },
    subscribe: (fn) => { subs.add(fn); return () => subs.delete(fn); },
  };
};
export const LangStore = LangStoreImpl();

export function useLang() {
  const [lang, setLang] = useState(LangStore.get());
  useEffect(() => LangStore.subscribe(setLang), []);
  const t = (v) => {
    if (v == null) return '';
    if (typeof v === 'string' || typeof v === 'number') return v;
    if (typeof v === 'object') return v[lang] || v.tr || v.en || '';
    return '';
  };
  return { lang, setLang: LangStore.set, t };
}

/* ======================= i18n ======================= */

const bi = (tr, en) => ({ tr, en });

// Static UI strings
export const UI = {
  nav: {
    home:     bi('Anasayfa', 'Home'),
    menu:     bi('Menü', 'Menu'),
    stores:   bi('Şubeler', 'Stores'),
    about:    bi('Hikaye', 'Story'),
    contact:  bi('İletişim', 'Contact'),
    fullMenu: bi('Tam Menü', 'Full Menu'),
  },
  hero: {
    scrollDown: bi('Aşağı kaydır', 'Scroll down'),
    signature:  bi('Signature', 'Signature'),
    freshNow:   bi('Taze', 'Fresh now'),
    rose:       bi('oat · rose · double', 'oat · rose · double'),
    butter:     bi('Fırından yeni çıktı', 'Fresh from the oven'),
  },
  menu: {
    kicker:       bi('— Menü', '— Menu'),
    title:        bi('Menü, küçük bir gezintidir.', 'The menu is a small journey.'),
    emTitle:      bi('küçük', 'small'),
    description:  bi('Kategoriyi seç, ara, sırala. Bir kartın üzerine tıkla — tarifin nasıl doğduğunu anlat.',
                     'Pick a category, search, sort. Tap a card — learn how each recipe came to life.'),
    search:       bi('Ara: rose, fıstık, 72h...', 'Search: rose, pistachio, 72h...'),
    openFull:     bi('Tam menüyü gör', 'View full menu'),
    items:        bi('ürün', 'items'),
    detail:       bi('Detay', 'Detail'),
    addToCart:    bi('Sepete ekle', 'Add to cart'),
    ingredients:  bi('İçindekiler', 'Ingredients'),
    pairing:      bi('Öneri', 'Pairing'),
    related:      bi('Benzerleri', 'Related'),
    price:        bi('Fiyat', 'Price'),
    noResult:     bi('Bu arama için sonuç yok.', 'No results for this search.'),
    tryAnother:   bi('Başka bir kelime dener misin?', 'Try another word?'),
    clearFilters: bi('Filtreleri Temizle', 'Clear Filters'),
    inStore:      bi('şubede', 'in store'),
    category:     bi('Kategori', 'Category'),
    brewTime:     bi('Hazırlama', 'Brew time'),
    ready:        bi('Hazır', 'Ready'),
    fromOven:     bi('Fırından', 'From the oven'),
    allItems:     bi('Tüm Menü', 'All Items'),
  },
  menuPage: {
    backToHome: bi('Ana sayfa', 'Home'),
    print:      bi('Yazdır', 'Print'),
    cover:      bi('Menü', 'Menu'),
    section:    bi('Bölüm', 'Section'),
    items:      bi('ÜRÜN', 'ITEMS'),
    notes:      bi('Notlar', 'Notes'),
    contact:    bi('İletişim', 'Contact'),
    branches:   bi('Şubeler', 'Branches'),
    notesBody:  bi('Alerjen bilgileri için baristaya danışabilirsin. Fiyatlar KDV dahildir. Haftanın bazı günleri özel tarifler eklenir.',
                   'Ask your barista for allergen info. Prices include VAT. Special recipes may appear on certain days.'),
    openingNote:bi('Her espresso, %100 specialty arabica — tek-origin, haftalık kavurma. Hamurlarımız 72 saat fermente, tereyağ Fransız. Bazı ürünlerde fıstık, süt, gluten bulunur.',
                   'Every espresso is 100% specialty arabica — single-origin, weekly roast. Doughs fermented 72h, French butter. Some items contain nuts, dairy, gluten.'),
  },
  sort: {
    curated:   bi('Öne Çıkanlar', 'Featured'),
    priceAsc:  bi('Fiyat · Artan', 'Price · Low to High'),
    priceDesc: bi('Fiyat · Azalan', 'Price · High to Low'),
    nameAsc:   bi('A — Z', 'A — Z'),
  },
  stores: {
    kicker:   bi('— Türkiye', '— Turkey'),
    city:     bi("Adana'da", 'in Adana'),
    directions: bi('Yol Tarifi', 'Directions'),
    call:     bi('Ara', 'Call'),
    open:     bi('Açık', 'Open'),
    cityZoom: bi('Şehir · Yakınlaştır', 'City · Zoom'),
    branchesCount: bi('şube', 'branches'),
  },
  ig: {
    live:       bi('Live', 'Live'),
    title:      bi('Sayfayı kaydır, tadı gözünle tat.', 'Scroll the feed, taste with your eyes.'),
    emTitle:    bi('kaydır', 'scroll'),
    followers:  bi('Takipçi', 'Followers'),
    follow:     bi('Takip et', 'Follow'),
    seeAll:     bi('Instagram\'da hepsini gör', 'See all on Instagram'),
    likes:      bi('beğeni', 'likes'),
    viewAllComments: bi('yorumun tümünü gör', 'view all comments'),
  },
  footer: {
    newsletterPlaceholder: bi('E-posta', 'Email'),
    newsletterJoin: bi('Katıl', 'Join'),
    privacy: bi('Gizlilik', 'Privacy'),
    kvkk:    bi('KVKK', 'Data'),
    cookies: bi('Çerezler', 'Cookies'),
  },
  contact: {
    name:       bi('Ad Soyad', 'Full Name'),
    namePh:     bi('Adın', 'Your name'),
    email:      bi('E-posta', 'Email'),
    emailPh:    bi('seni@nasıl.yazalım', 'you@how.shall.we.write'),
    subject:    bi('Konu', 'Subject'),
    subjectPh:  bi('Rezervasyon, işbirliği...', 'Booking, partnership...'),
    message:    bi('Mesaj', 'Message'),
    messagePh:  bi('Anlat bakalım...', 'Tell us...'),
    kvkk:       bi('Gönderim ile KVKK aydınlatma metnini onaylamış olursun.',
                   'By submitting you agree to our privacy notice.'),
    submit:     bi('Gönder', 'Send'),
    thanks:     bi('Teşekkürler — en kısa sürede döneriz.', 'Thanks — we\'ll get back to you shortly.'),
  },
  common: {
    close: bi('Kapat', 'Close'),
  },
};

/* ======================= Default content ======================= */

export const DEFAULT_CONTENT = {
  brand: {
    name: 'GLAMM',
    tagline: bi('Coffee & Bakery', 'Coffee & Bakery'),
    city: bi('Adana', 'Adana'),
    seasonLabel: bi('Sezon · Rose Latte ’26', 'Season · Rose Latte ’26'),
    instagram: '@glammcoffee',
    phone: '+90 322 000 00 01',
    email: 'hi@glamm.coffee',
    primaryColor: '#4a2e1e',
    accent: '#e9b7a6',
    logo: '/assets/glamm.png',
    favicon: '/assets/glamm.png',
  },

  seo: {
    title: 'GLAMM Coffee & Bakery — Not just coffee. A glamm ritual.',
    description: bi(
      "GLAMM Coffee & Bakery — Adana'da üç şube. Specialty kahve, 72 saatlik hamur, küçük ritüeller.",
      'GLAMM Coffee & Bakery — three stores in Adana. Specialty coffee, 72-hour dough, little rituals.'
    ),
    keywords: bi(
      'glamm, glamm coffee, adana kahve, specialty coffee, kahve dükkanı, bakery, adana bakery',
      'glamm, glamm coffee, adana coffee, specialty coffee, coffee shop, bakery, adana bakery'
    ),
    lang: 'tr',
    themeColor: '#4a2e1e',
    ogTitle: 'GLAMM Coffee & Bakery',
    ogDescription: bi(
      'Boutique kahve ve fırından, minimal ve zarif bir lifestyle deneyimi.',
      'A minimal, elegant lifestyle experience from a boutique coffee roaster and bakery.'
    ),
    ogImage: '/assets/glamm-logo.png',
    ogUrl: '',
    twitterCard: 'summary_large_image',
    canonical: '',
  },

  hero: {
    title: bi('Kahveden öte.\nBir Glamm ritüeli.', 'Not just coffee.\nA Glamm ritual.'),
    sub: bi('Butik kahve ve el yapımı fırından; minimal, zarif bir yaşam deneyimi.',
            'A minimal, elegant lifestyle experience from a boutique coffee roaster and bakery.'),
    ctaPrimary: bi('Menüyü Keşfet', 'Explore menu'),
    ctaSecondary: bi('Şubeleri Gör', 'Find stores'),
    showSteam: true,
    showBeans: true,
    cupSpeed: 18,
    beanCount: 18,
    rating: '4.9',
    ratingLabel: bi('Kahve severler', 'Coffee lovers'),
    stat: '%100',
    statLabel: bi('Arabica · Specialty grade', 'Arabica · Specialty grade'),
    showLogoOnCup: true,
    cupStyle: 'porcelain',
    // Chip cards around the 3D cup — menu item ids (null = auto-pick from menu)
    signatureChip: {
      show: true,
      menuId: null,
      labelOverride: { tr: '', en: '' },
    },
    freshChip: {
      show: true,
      menuId: null,
      labelOverride: { tr: '', en: '' },
    },
  },

  experience: [
    { k: '01', t: bi('Yavaş demlenmiş', 'Slow-crafted'),
               d: bi('Her espresso, %100 specialty arabica. Tek origin, haftalık kavurma.',
                     'Every espresso is 100% specialty arabica. Single-origin, weekly roast.') },
    { k: '02', t: bi('Fırından', 'From the oven'),
               d: bi('72 saat fermente hamur, Fransız tereyağı. Her sabah yeniden.',
                     '72-hour fermented dough, French butter. Every single morning.') },
    { k: '03', t: bi('Küçük ritüeller', 'Little rituals'),
               d: bi('Menü, ışık ve müzik — bir sayfa kaydırır gibi huzur.',
                     'Menu, light, music — peace like turning a page.') },
    { k: '04', t: bi('El emeği', 'Handcrafted'),
               d: bi('Her tabak ve her bardak, baristalarımızın elinden. Aynı özen, aynı sıcaklık.',
                     'Every plate and every cup, made by hand. Same care, same warmth.') },
  ],

  featured: [
    { name: bi('Rose Latte', 'Rose Latte'),               tone: 'rose',      image: '', label: bi('ROSE LATTE · SIGNATURE', 'ROSE LATTE · SIGNATURE'),       price: '₺125' },
    { name: bi('Basque Cheesecake', 'Basque Cheesecake'), tone: 'cream',     image: '', label: bi('BASQUE · TOP BURNT', 'BASQUE · TOP BURNT'),               price: '₺145' },
    { name: bi('Butter Croissant', 'Butter Croissant'),   tone: 'butter',    image: '', label: bi('CROISSANT · 72H', 'CROISSANT · 72H'),                     price: '₺80'  },
    { name: bi('Pistachio Mocha', 'Pistachio Mocha'),     tone: 'pistachio', image: '', label: bi('PISTACHIO MOCHA · BESTSELLER', 'PISTACHIO MOCHA · BESTSELLER'), price: '₺135' },
  ],

  campaigns: {
    hero: {
      label: bi('Yeni Sezon', 'New Season'),
      title: bi('Rose Latte · sezon tazeliği.', 'Rose Latte · a fresh new season.'),
      body: bi('Gül şurubu, yulaf sütü ve çift shot specialty espresso. Bu sezonun imzası, şubelerimizde.',
               'Rose syrup, oat milk and a double shot of specialty espresso. The season\'s signature, in our stores.'),
      cta1: bi('Menüyü Gör', 'See the Menu'),
      cta1Link: '#/tam-menu',
      cta2: bi('Şubeleri Bul', 'Find Stores'),
      cta2Link: '#stores',
    },
    side: [
      { label: bi('Hafta Sonu', 'Weekend'), title: bi('İkinci filtre kahve hediye.', 'Second filter coffee on us.'),
        note: bi('Her Cumartesi & Pazar · tüm şubeler.', 'Every Sat & Sun · all stores.'), tone: 'white', link: '#stores' },
      { label: bi('Özel Tarifler', 'Specials'), title: bi('Haftanın imza tarifleri.', 'Signature recipes of the week.'),
        note: bi('Barista seçkisi · değişken menü.', 'Barista picks · rotating menu.'), tone: 'rose', link: '#/tam-menu' },
    ],
  },

  menu: {
    categories: [
      { id: 'all', label: bi('Hepsi', 'All'), image: '', tone: 'cream' },
      { id: 'sicak', label: bi('Sıcak İçecekler', 'Hot Drinks'), image: '', tone: 'cocoa' },
      { id: 'soguk', label: bi('Soğuk İçecekler', 'Cold Drinks'), image: '', tone: 'latte' },
      { id: 'spesiyal', label: bi('Spesiyaller', 'Specialty'), image: '', tone: 'rose' },
      { id: 'fruit', label: bi('Fruit Drinks', 'Fruit Drinks'), image: '', tone: 'rose' },
      { id: 'soft', label: bi('Soft İçecekler', 'Soft Drinks'), image: '', tone: 'butter' },
      { id: 'ekstra', label: bi('Ekstralar', 'Extras'), image: '', tone: 'cream' },
      { id: 'kahvalti', label: bi('Kahvaltı', 'Breakfast'), image: '', tone: 'butter' },
      { id: 'tatli', label: bi('Tatlılar', 'Desserts'), image: '', tone: 'cream' },
      { id: 'demleme', label: bi('Demleme Ekipmanları', 'Brew Equipment'), image: '', tone: 'cocoa' },
      { id: 'cay', label: bi('Çaylar', 'Teas'), image: '', tone: 'butter' },
    ],
    items: [
      // Sıcak İçecekler
      { id:101, cat:'sicak', name: bi('Americano', 'Americano'), price: '₺200', tag: '', note: bi('', ''), tone: 'cocoa', image: '' },
      { id:102, cat:'sicak', name: bi('Cappucino', 'Cappuccino'), price: '₺210', tag: '', note: bi('', ''), tone: 'cocoa', image: '' },
      { id:103, cat:'sicak', name: bi('Cortado', 'Cortado'), price: '₺180', tag: '', note: bi('', ''), tone: 'cocoa', image: '' },
      { id:104, cat:'sicak', name: bi('Espresso', 'Espresso'), price: '₺160', tag: '', note: bi('', ''), tone: 'cocoa', image: '' },
      { id:105, cat:'sicak', name: bi('Espresso Macchiato', 'Espresso Macchiato'), price: '₺170', tag: '', note: bi('', ''), tone: 'cocoa', image: '' },
      { id:106, cat:'sicak', name: bi('Filtre Kahve', 'Filter Coffee'), price: '₺200', tag: '', note: bi('', ''), tone: 'cocoa', image: '' },
      { id:107, cat:'sicak', name: bi('Flat White', 'Flat White'), price: '₺190', tag: '', note: bi('', ''), tone: 'cocoa', image: '' },
      { id:108, cat:'sicak', name: bi('Latte', 'Latte'), price: '₺210', tag: '', note: bi('', ''), tone: 'cocoa', image: '' },
      { id:109, cat:'sicak', name: bi('Ristretto', 'Ristretto'), price: '₺160', tag: '', note: bi('', ''), tone: 'cocoa', image: '' },
      { id:110, cat:'sicak', name: bi('Türk Kahvesi', 'Turkish Coffee'), price: '₺130', tag: '', note: bi('', ''), tone: 'cocoa', image: '' },
      { id:111, cat:'sicak', name: bi('SICAK ÇİKOLATA', 'Hot Chocolate'), price: '₺200', tag: '', note: bi('', ''), tone: 'cocoa', image: '' },
      { id:112, cat:'sicak', name: bi('SAHLEP', 'Sahlep'), price: '₺220', tag: '', note: bi('', ''), tone: 'cocoa', image: '' },
      { id:113, cat:'sicak', name: bi('V60', 'V60'), price: '₺260', tag: '', note: bi('SICAK SERVİS EDİLİR', 'SICAK SERVİS EDİLİR'), tone: 'cocoa', image: '' },
      { id:114, cat:'sicak', name: bi('Toffe Nut Latte', 'Toffee Nut Latte'), price: '₺245', tag: '', note: bi('', ''), tone: 'cocoa', image: '' },
      { id:115, cat:'sicak', name: bi('LATTE MACCHIATO', 'Latte Macchiato'), price: '₺210', tag: '', note: bi('', ''), tone: 'cocoa', image: '' },

      // Soğuk İçecekler
      { id:116, cat:'soguk', name: bi('Cold Brew', 'Cold Brew'), price: '₺250', tag: '', note: bi('', ''), tone: 'latte', image: '' },
      { id:117, cat:'soguk', name: bi('Espresso Shake', 'Espresso Shake'), price: '₺210', tag: '', note: bi('', ''), tone: 'latte', image: '' },
      { id:118, cat:'soguk', name: bi('Ice Americano', 'Iced Americano'), price: '₺210', tag: '', note: bi('', ''), tone: 'latte', image: '' },
      { id:119, cat:'soguk', name: bi('Ice Filtre', 'Iced Filter'), price: '₺210', tag: '', note: bi('', ''), tone: 'latte', image: '' },
      { id:120, cat:'soguk', name: bi('Ice Flat White', 'Iced Flat White'), price: '₺210', tag: '', note: bi('', ''), tone: 'latte', image: '' },
      { id:121, cat:'soguk', name: bi('Ice Latte', 'Iced Latte'), price: '₺220', tag: '', note: bi('', ''), tone: 'latte', image: '' },
      { id:122, cat:'soguk', name: bi('Frappe', 'Frappe'), price: '₺250', tag: '', note: bi('', ''), tone: 'latte', image: '' },
      { id:123, cat:'soguk', name: bi('ICE MATCHA latte', 'Iced Matcha Latte'), price: '₺265', tag: '', note: bi('', ''), tone: 'latte', image: '' },
      { id:124, cat:'soguk', name: bi('CHURCHILL', 'Churchill'), price: '₺170', tag: '', note: bi('', ''), tone: 'latte', image: '' },

      // Spesiyaller
      { id:125, cat:'spesiyal', name: bi('Caffe Mocha', 'Caffè Mocha'), price: '₺240', tag: '', note: bi('', ''), tone: 'rose', image: '' },
      { id:126, cat:'spesiyal', name: bi('Caramel Macchiato', 'Caramel Macchiato'), price: '₺245', tag: '', note: bi('', ''), tone: 'rose', image: '' },
      { id:127, cat:'spesiyal', name: bi('Ice Caffe Mocha', 'Iced Caffè Mocha'), price: '₺250', tag: '', note: bi('', ''), tone: 'rose', image: '' },
      { id:128, cat:'spesiyal', name: bi('Ice Caramel Macchiato', 'Iced Caramel Macchiato'), price: '₺260', tag: '', note: bi('', ''), tone: 'rose', image: '' },
      { id:129, cat:'spesiyal', name: bi('Ice White Mocha', 'Iced White Mocha'), price: '₺250', tag: '', note: bi('', ''), tone: 'rose', image: '' },
      { id:130, cat:'spesiyal', name: bi('White Mocha', 'White Mocha'), price: '₺240', tag: '', note: bi('', ''), tone: 'rose', image: '' },
      { id:131, cat:'spesiyal', name: bi('Toffe Nut Latte', 'Toffee Nut Latte'), price: '₺245', tag: '', note: bi('', ''), tone: 'rose', image: '' },
      { id:132, cat:'spesiyal', name: bi('Chai Tea Latte', 'Chai Tea Latte'), price: '₺220', tag: '', note: bi('', ''), tone: 'rose', image: '' },
      { id:133, cat:'spesiyal', name: bi('ICE CHAİ TEA LATTE', 'Iced Chai Tea Latte'), price: '₺235', tag: '', note: bi('', ''), tone: 'rose', image: '' },
      { id:134, cat:'spesiyal', name: bi('ICE FİLTRE', 'Iced Filter'), price: '₺210', tag: '', note: bi('', ''), tone: 'rose', image: '' },
      { id:135, cat:'spesiyal', name: bi('ICE FLAT WHİTE', 'Iced Flat White'), price: '₺200', tag: '', note: bi('', ''), tone: 'rose', image: '' },
      { id:136, cat:'spesiyal', name: bi('ICE TOFFE NUT LATTE', 'Iced Toffee Nut Latte'), price: '₺255', tag: '', note: bi('', ''), tone: 'rose', image: '' },
      { id:137, cat:'spesiyal', name: bi('ICE CAPPUCCİNO', 'Iced Cappuccino'), price: '₺220', tag: '', note: bi('', ''), tone: 'rose', image: '' },
      { id:138, cat:'spesiyal', name: bi('Matcha Latte', 'Matcha Latte'), price: '₺255', tag: '', note: bi('', ''), tone: 'rose', image: '' },

      // Fruit Drinks
      { id:139, cat:'fruit', name: bi('ORMAN MEYVELİ FROZEN', 'Forest Berry Frozen'), price: '₺215', tag: '', note: bi('SOĞUK SERVİS EDİLİR', 'SOĞUK SERVİS EDİLİR'), tone: 'rose', image: '' },
      { id:140, cat:'fruit', name: bi('COOL LİME MİNT', 'Cool Lime Mint'), price: '₺250', tag: '', note: bi('SOĞUK SERVİS EDİLİR', 'SOĞUK SERVİS EDİLİR'), tone: 'rose', image: '' },
      { id:141, cat:'fruit', name: bi('ÇİLEKLİ MİLKSHAKE', 'Strawberry Milkshake'), price: '₺215', tag: '', note: bi('SOĞUK SERVİS EDİLİR', 'SOĞUK SERVİS EDİLİR'), tone: 'rose', image: '' },
      { id:142, cat:'fruit', name: bi('ÇİKOLATALI MİLKSHAKE', 'Chocolate Milkshake'), price: '₺215', tag: '', note: bi('SOĞUK SERVİS EDİLİR', 'SOĞUK SERVİS EDİLİR'), tone: 'rose', image: '' },
      { id:143, cat:'fruit', name: bi('VANİLYA MİLKSHAKE', 'Vanilla Milkshake'), price: '₺215', tag: '', note: bi('SOĞUK SERVİS EDİLİR', 'SOĞUK SERVİS EDİLİR'), tone: 'rose', image: '' },
      { id:144, cat:'fruit', name: bi('STRAWBERRY FROZEN', 'Strawberry Frozen'), price: '₺215', tag: '', note: bi('SOĞUK SERVİS EDİLİR', 'SOĞUK SERVİS EDİLİR'), tone: 'rose', image: '' },
      { id:145, cat:'fruit', name: bi('MANGO FROZEN', 'Mango Frozen'), price: '₺215', tag: '', note: bi('SOĞUK SERVİS EDİLİR', 'SOĞUK SERVİS EDİLİR'), tone: 'rose', image: '' },
      { id:146, cat:'fruit', name: bi('STRAWBERRY COOL LİME', 'Strawberry Cool Lime'), price: '₺250', tag: '', note: bi('SOĞUK SERVİS EDİLİR', 'SOĞUK SERVİS EDİLİR'), tone: 'rose', image: '' },
      { id:147, cat:'fruit', name: bi('MANGO COOL LİME', 'Mango Cool Lime'), price: '₺250', tag: '', note: bi('SOĞUK SERVİS EDİLİR', 'SOĞUK SERVİS EDİLİR'), tone: 'rose', image: '' },
      { id:148, cat:'fruit', name: bi('STRAWBERRY HİBİSCUS', 'Strawberry Hibiscus'), price: '₺250', tag: '', note: bi('SOĞUK SERVİS EDİLİR', 'SOĞUK SERVİS EDİLİR'), tone: 'rose', image: '' },
      { id:149, cat:'fruit', name: bi('YABAN MERSİNLİ HİBİSCUS', 'Blueberry Hibiscus'), price: '₺235', tag: '', note: bi('SOĞUK SERVİS EDİLİR', 'SOĞUK SERVİS EDİLİR'), tone: 'rose', image: '' },
      { id:150, cat:'fruit', name: bi('LİMONATA', 'Lemonade'), price: '₺200', tag: '', note: bi('', ''), tone: 'rose', image: '' },
      { id:151, cat:'fruit', name: bi('Golden Ruby Splash', 'Golden Ruby Splash'), price: '₺220', tag: '', note: bi('', ''), tone: 'rose', image: '' },

      // Soft İçecekler
      { id:152, cat:'soft', name: bi('Beyoğlu Klasik', 'Beyoğlu Classic'), price: '₺100', tag: '', note: bi('', ''), tone: 'butter', image: '' },
      { id:153, cat:'soft', name: bi('Beyoğlu Portakal', 'Beyoğlu Orange'), price: '₺100', tag: '', note: bi('', ''), tone: 'butter', image: '' },
      { id:154, cat:'soft', name: bi('Beyoğlu Reyhan', 'Beyoğlu Basil'), price: '₺100', tag: '', note: bi('', ''), tone: 'butter', image: '' },
      { id:155, cat:'soft', name: bi('Beyoğlu Zencefil', 'Beyoğlu Ginger'), price: '₺100', tag: '', note: bi('', ''), tone: 'butter', image: '' },
      { id:156, cat:'soft', name: bi('Uludağ Premium Soda', 'Uludağ Premium Soda'), price: '₺90', tag: '', note: bi('', ''), tone: 'butter', image: '' },
      { id:157, cat:'soft', name: bi('Marsu Cam', 'Marsu Glass'), price: '₺50', tag: '', note: bi('', ''), tone: 'butter', image: '' },

      // Ekstralar
      { id:158, cat:'ekstra', name: bi('Balkabağı', 'Pumpkin'), price: '₺35', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:159, cat:'ekstra', name: bi('Böğürtlen', 'Blackberry'), price: '₺35', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:160, cat:'ekstra', name: bi('Butterscotch Sos', 'Butterscotch Sauce'), price: '₺35', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:161, cat:'ekstra', name: bi('Çikolata Sos', 'Chocolate Sauce'), price: '₺35', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:162, cat:'ekstra', name: bi('Çilek Püresi', 'Strawberry Purée'), price: '₺35', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:163, cat:'ekstra', name: bi('Fındık', 'Hazelnut'), price: '₺35', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:164, cat:'ekstra', name: bi('Karamel', 'Caramel'), price: '₺35', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:165, cat:'ekstra', name: bi('Karamel Sos', 'Caramel Sauce'), price: '₺35', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:166, cat:'ekstra', name: bi('Kavrulmuş Badem', 'Roasted Almond'), price: '₺35', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:167, cat:'ekstra', name: bi('Kremaşanti', 'Whipped Cream'), price: '₺35', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:168, cat:'ekstra', name: bi('Lime', 'Lime'), price: '₺35', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:169, cat:'ekstra', name: bi('Mango Püresi', 'Mango Purée'), price: '₺35', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:170, cat:'ekstra', name: bi('Nane', 'Mint'), price: '₺35', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:171, cat:'ekstra', name: bi('Orman Meyvesi', 'Forest Berry'), price: '₺35', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:172, cat:'ekstra', name: bi('Passion Fruit', 'Passion Fruit'), price: '₺35', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:173, cat:'ekstra', name: bi('Vanilya', 'Vanilla'), price: '₺35', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:174, cat:'ekstra', name: bi('Cookie', 'Cookie'), price: '₺35', tag: '', note: bi('', ''), tone: 'cream', image: '' },

      // Kahvaltı
      { id:175, cat:'kahvalti', name: bi('Granola Bowl', 'Granola Bowl'), price: '₺350', tag: '', note: bi('Doğal bal ile tatlandırılmış ev yapımı granola, süt veya yoğurtla, taze meyveler eşliğinde servis edilir. ballı çikolatalı orman meyvesi', 'Doğal bal ile tatlandırılmış ev yapımı granola, süt veya yoğurtla, taze meyveler eşliğinde servis edilir. ballı çikolatalı orman meyvesi'), tone: 'butter', image: '' },
      { id:176, cat:'kahvalti', name: bi('Kavurmalı Tost', 'Braised Beef Toast'), price: '₺430', tag: '', note: bi('Bazlama ekmeği arasında kavurma, kaşar, taneli hardal, domates sosu, ceviz, jalapeno turşu ve taze nane ile hazırlanan özel tost.', 'Bazlama ekmeği arasında kavurma, kaşar, taneli hardal, domates sosu, ceviz, jalapeno turşu ve taze nane ile hazırlanan özel tost.'), tone: 'butter', image: '' },
      { id:177, cat:'kahvalti', name: bi('Pesto Soslu Hindi Fümeli Tost', 'Pesto Smoked Turkey Toast'), price: '₺400', tag: '', note: bi('Ev yapımı pesto sos, hindi füme, kaşar, kuru domates ve roka ile hafif ve lezzetli bir tost deneyimi.', 'Ev yapımı pesto sos, hindi füme, kaşar, kuru domates ve roka ile hafif ve lezzetli bir tost deneyimi.'), tone: 'butter', image: '' },
      { id:178, cat:'kahvalti', name: bi('5 Peynirli Tost', '5-Cheese Toast'), price: '₺370', tag: '', note: bi('Beyaz peynir, cheddar ve kaşarın eşsiz uyumu ile hazırlanmış tost.', 'Beyaz peynir, cheddar ve kaşarın eşsiz uyumu ile hazırlanmış tost.'), tone: 'butter', image: '' },
      { id:179, cat:'kahvalti', name: bi('Morning Breakfast', 'Morning Breakfast'), price: '₺420', tag: '', note: bi('Glamm omlet, avokado, hindi füme, taze sebzeler, peynir çeşitleri, nutella ve cevizli ezme ile zengin bir kahvaltı keyfi! (1 bardak çay)', 'Glamm omlet, avokado, hindi füme, taze sebzeler, peynir çeşitleri, nutella ve cevizli ezme ile zengin bir kahvaltı keyfi! (1 bardak çay)'), tone: 'butter', image: '' },
      { id:180, cat:'kahvalti', name: bi('Roastbeef tost', 'Roastbeef Toast'), price: '₺430', tag: '', note: bi('Ev yapımı biberli sos, dana rozbif, tel peynir ve roka ile zengin bir sandviç.', 'Ev yapımı biberli sos, dana rozbif, tel peynir ve roka ile zengin bir sandviç.'), tone: 'butter', image: '' },
      { id:181, cat:'kahvalti', name: bi('Combo Tost', 'Combo Toast'), price: '₺430', tag: '', note: bi('Dana Füme-Hindi Füme-Hardal-Domatesli Pesto-Roka-Toz Ceviz-Kaşar-Chedar-Susam', 'Dana Füme-Hindi Füme-Hardal-Domatesli Pesto-Roka-Toz Ceviz-Kaşar-Chedar-Susam'), tone: 'butter', image: '' },
      { id:182, cat:'kahvalti', name: bi('Avokado Tartini', 'Avocado Tartine'), price: '₺380', tag: '', note: bi('Kızarmış ekşi mayalı ekmek üzeri avokado sos üstüne poşe yumurta ve kızarmış hellim peyniri ile servis edilir.', 'Kızarmış ekşi mayalı ekmek üzeri avokado sos üstüne poşe yumurta ve kızarmış hellim peyniri ile servis edilir.'), tone: 'butter', image: '' },
      { id:183, cat:'kahvalti', name: bi('Eggsplosion Toast', 'Eggsplosion Toast'), price: '₺420', tag: '', note: bi('', ''), tone: 'butter', image: '' },
      { id:184, cat:'kahvalti', name: bi('GLAMM BREAKFAST', 'Glamm Breakfast'), price: '₺650', tag: '', note: bi('glamm omlet, avokado, hindi füme,dana füme, taze sebzeler, peynir çeşitleri, nutella , frankfurter sosis ,ızgara ananas, muhammara ile zengin bir kahvaltı keyfi! (1 bardak çay)', 'glamm omlet, avokado, hindi füme,dana füme, taze sebzeler, peynir çeşitleri, nutella , frankfurter sosis ,ızgara ananas, muhammara ile zengin bir kahvaltı keyfi! (1 bardak çay)'), tone: 'butter', image: '' },
      { id:185, cat:'kahvalti', name: bi('ACILI COMBO TOST', 'Spicy Combo Toast'), price: '₺430', tag: '', note: bi('', ''), tone: 'butter', image: '' },
      { id:186, cat:'kahvalti', name: bi('OMLET', 'Omelet'), price: '₺150', tag: '', note: bi('', ''), tone: 'butter', image: '' },
      { id:187, cat:'kahvalti', name: bi('SANDVİÇ', 'Sandwich'), price: '₺300', tag: '', note: bi('', ''), tone: 'butter', image: '' },

      // Tatlılar
      { id:188, cat:'tatli', name: bi('Beyaz Çikolatalı Brownie', 'White Chocolate Brownie'), price: '₺290', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:189, cat:'tatli', name: bi('Lotus Cheesecake', 'Lotus Cheesecake'), price: '₺290', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:190, cat:'tatli', name: bi('Orman Meyveli Cheesecake', 'Forest Berry Cheesecake'), price: '₺290', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:191, cat:'tatli', name: bi('Cookie', 'Cookie'), price: '₺225', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:192, cat:'tatli', name: bi('Çilekli Magnolia', 'Strawberry Magnolia'), price: '₺270', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:193, cat:'tatli', name: bi('San Sebastian', 'San Sebastian'), price: '₺300', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:194, cat:'tatli', name: bi('Tiramisu', 'Tiramisu'), price: '₺235', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:195, cat:'tatli', name: bi('LOTUS MAGNOLIA', 'Lotus Magnolia'), price: '₺250', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:196, cat:'tatli', name: bi('Orman Meyveli Magnolia', 'Forest Berry Magnolia'), price: '₺270', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:197, cat:'tatli', name: bi('Glamm Green', 'Glamm Green'), price: '₺275', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:198, cat:'tatli', name: bi('çikolatalı Sneakers', 'Chocolate Sneakers'), price: '₺275', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:199, cat:'tatli', name: bi('Cream Puff', 'Cream Puff'), price: '₺275', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:200, cat:'tatli', name: bi('Çilek Tart', 'Strawberry Tart'), price: '₺275', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:201, cat:'tatli', name: bi('Mom Cake', 'Mom Cake'), price: '₺250', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:202, cat:'tatli', name: bi('KARAMELLİ ELMALI CHEESCAKE', 'Caramel Apple Cheesecake'), price: '₺290', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:203, cat:'tatli', name: bi('İTALYAN TURTASI', 'Italian Tart'), price: '₺290', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:204, cat:'tatli', name: bi('Nutella Cookie', 'Nutella Cookie'), price: '₺290', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:205, cat:'tatli', name: bi('PROFİTEROL', 'Profiterole'), price: '₺250', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:206, cat:'tatli', name: bi('COOKİE BROWNİE', 'Cookie Brownie'), price: '₺275', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:207, cat:'tatli', name: bi('BERRY CROWN', 'Berry Crown'), price: '₺250', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:208, cat:'tatli', name: bi('ÇİKOLATALI MONO PASTA', 'Chocolate Mono Cake'), price: '₺260', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:209, cat:'tatli', name: bi('FISTIKLI SEBASTİAN', 'Pistachio San Sebastian'), price: '₺320', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:210, cat:'tatli', name: bi('Glamm Ball', 'Glamm Ball'), price: '₺275', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:211, cat:'tatli', name: bi('KURABİYE', 'Cookie'), price: '₺175', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:212, cat:'tatli', name: bi('MOCHA CAKE', 'Mocha Cake'), price: '₺250', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:213, cat:'tatli', name: bi('MOZAİK PASTA DİLİM', 'Mosaic Cake Slice'), price: '₺250', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:214, cat:'tatli', name: bi('MUFFİN', 'Muffin'), price: '₺170', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:215, cat:'tatli', name: bi('ORMAN MEYVELİ TART', 'Forest Berry Tart'), price: '₺275', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:216, cat:'tatli', name: bi('PORTAKALLI SEBASTİAN', 'Orange San Sebastian'), price: '₺320', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:217, cat:'tatli', name: bi('SÜTLAÇ', 'Rice Pudding'), price: '₺270', tag: '', note: bi('', ''), tone: 'cream', image: '' },
      { id:218, cat:'tatli', name: bi('GLAMM ORANGE', 'Glamm Orange'), price: '₺275', tag: '', note: bi('', ''), tone: 'cream', image: '' },

      // Demleme Ekipmanları
      { id:219, cat:'demleme', name: bi('Hario Ceramic Coffee Mill Skerton N', 'Hario Ceramic Coffee Mill Skerton N'), price: '₺2.250', tag: '', note: bi('', ''), tone: 'cocoa', image: '' },
      { id:220, cat:'demleme', name: bi('Hario V60 Ceramic Dripper 02 Set', 'Hario V60 Ceramic Dripper 02 Set'), price: '₺1.950', tag: '', note: bi('', ''), tone: 'cocoa', image: '' },
      { id:221, cat:'demleme', name: bi('Hario V60 Coffee Dripper (Kırmızı)', 'Hario V60 Coffee Dripper (Kırmızı)'), price: '₺1.500', tag: '', note: bi('', ''), tone: 'cocoa', image: '' },
      { id:222, cat:'demleme', name: bi('Hario V60 Coffee Paper Filter', 'Hario V60 Coffee Paper Filter'), price: '₺220', tag: '', note: bi('', ''), tone: 'cocoa', image: '' },
      { id:223, cat:'demleme', name: bi('Hario V60 Drip Decanter', 'Hario V60 Drip Decanter'), price: '₺1.500', tag: '', note: bi('', ''), tone: 'cocoa', image: '' },
      { id:224, cat:'demleme', name: bi('Hario V60 Glass Server (600ml)', 'Hario V60 Glass Server (600ml)'), price: '₺1.350', tag: '', note: bi('', ''), tone: 'cocoa', image: '' },

      // Çaylar
      { id:225, cat:'cay', name: bi('ÇAY', 'Tea'), price: '₺90', tag: '', note: bi('', ''), tone: 'butter', image: '' },
      { id:226, cat:'cay', name: bi('Çay Double', 'Double Tea'), price: '₺110', tag: '', note: bi('', ''), tone: 'butter', image: '' },
      { id:227, cat:'cay', name: bi('Tea Apricot Peach Oolong', 'Apricot Peach Oolong'), price: '₺250', tag: '', note: bi('oolong, siyah çay, kayısı, şeftali, kadife & aspir çiçeği', 'oolong, siyah çay, kayısı, şeftali, kadife & aspir çiçeği'), tone: 'butter', image: '' },
      { id:228, cat:'cay', name: bi('Tea Ayurveda Herbs&Ginger', 'Ayurveda Herbs & Ginger'), price: '₺250', tag: '', note: bi('zencefil, anason, rezene, meyan kökü, limon otu, karabiber', 'zencefil, anason, rezene, meyan kökü, limon otu, karabiber'), tone: 'butter', image: '' },
      { id:229, cat:'cay', name: bi('Tea Get The Power', 'Get The Power'), price: '₺250', tag: '', note: bi('amber çiçeği, böğürtlen yaprakları, kola bitkisi, mürver', 'amber çiçeği, böğürtlen yaprakları, kola bitkisi, mürver'), tone: 'butter', image: '' },
      { id:230, cat:'cay', name: bi('Tea Ginger Twist', 'Ginger Twist'), price: '₺250', tag: '', note: bi('yeşil çay, ayva, zencefil, aynısafa, safran', 'yeşil çay, ayva, zencefil, aynısafa, safran'), tone: 'butter', image: '' },
      { id:231, cat:'cay', name: bi('Tea Greenleaf', 'Green Leaf'), price: '₺250', tag: '', note: bi('yeşil çay', 'yeşil çay'), tone: 'butter', image: '' },
      { id:232, cat:'cay', name: bi('Tea Jasmine Pearls', 'Jasmine Pearls'), price: '₺250', tag: '', note: bi('yeşil çay, yasemin', 'yeşil çay, yasemin'), tone: 'butter', image: '' },
      { id:233, cat:'cay', name: bi('Tea Morgentau', 'Morgentau'), price: '₺250', tag: '', note: bi('yeşil çay, mango, ayçiçeği, gül, peygamber çiçeği', 'yeşil çay, mango, ayçiçeği, gül, peygamber çiçeği'), tone: 'butter', image: '' },
      { id:234, cat:'cay', name: bi('Tea Vanilia Rooibos', 'Vanilla Rooibos'), price: '₺250', tag: '', note: bi('rooibos, vanilya', 'rooibos, vanilya'), tone: 'butter', image: '' },
      { id:235, cat:'cay', name: bi('Tea Chinese Spring', 'Chinese Spring'), price: '₺250', tag: '', note: bi('beyaz çay, çarkıfelek meyvesi, ebegümeci, aynısafa', 'beyaz çay, çarkıfelek meyvesi, ebegümeci, aynısafa'), tone: 'butter', image: '' },
      { id:236, cat:'cay', name: bi('Tea Milky Oolong', 'Milky Oolong'), price: '₺250', tag: '', note: bi('oolong çayı, doğal süt aroması', 'oolong çayı, doğal süt aroması'), tone: 'butter', image: '' },
    ],
  },

  // Gerçek Adana şubeleri. Adana Turkey SVG'de ~55.1% x, 59.8% y.
  // Pin'leri küçük farklarla yerleştiriyoruz ki üst üste binmesin.
  branches: [
    { id: 1, name: bi('Glamm Coffee & Bakery Ziyapaşa', 'Glamm Coffee & Bakery Ziyapaşa'),
             addr: bi('Kurtuluş, Şinasi Efendi Cd. Akpak Apt. Altı, 01010 Seyhan/Adana',
                      'Kurtuluş, Şinasi Efendi Cd. Akpak Apt. Altı, 01010 Seyhan/Adana'),
             hours: '08:30 – 00:00',
             tag: bi('Flagship', 'Flagship'),
             x: 55.3, y: 60.0,
             phone: '+90 533 693 03 36',
             rating: '4.4',
             mapLink: 'https://www.google.com/maps/search/Glamm+Coffee+%26+Bakery+Ziyapa%C5%9Fa+Seyhan+Adana' },
    { id: 2, name: bi('Glamm Bakery & Coffee', 'Glamm Bakery & Coffee'),
             addr: bi('Yurt, Süleyman Demirel Blv. no:57/A, 01170 Çukurova/Adana',
                      'Yurt, Süleyman Demirel Blv. no:57/A, 01170 Çukurova/Adana'),
             hours: '08:00 – 23:30',
             tag: '',
             x: 54.7, y: 59.3,
             phone: '',
             rating: '4.6',
             mapLink: 'https://www.google.com/maps/search/Glamm+Bakery+%26+Coffee+S%C3%BCleyman+Demirel+%C3%87ukurova+Adana' },
    { id: 3, name: bi('Glamm Bakery & Coffee Uğur Mumcu', 'Glamm Bakery & Coffee Uğur Mumcu'),
             addr: bi('Güzelyalı, 81150. Sk. NO: 3 D:2A, 01770 Çukurova/Adana',
                      'Güzelyalı, 81150. Sk. NO: 3 D:2A, 01770 Çukurova/Adana'),
             hours: '08:00 – 23:00',
             tag: bi('Yeni', 'New'),
             x: 56.1, y: 59.5,
             phone: '+90 532 371 81 01',
             rating: '3.4',
             mapLink: 'https://www.google.com/maps/search/Glamm+Bakery+%26+Coffee+U%C4%9Fur+Mumcu+%C3%87ukurova+Adana' },
  ],

  stories: [
    { id: 1, title: bi('Rose Latte', 'Rose Latte'),        image: '', tone: 'rose' },
    { id: 2, title: bi('Matcha', 'Matcha'),                image: '', tone: 'pistachio' },
    { id: 3, title: bi('Fırından', 'From oven'),           image: '', tone: 'butter' },
    { id: 4, title: bi('Altın Saat', 'Golden Hour'),       image: '', tone: 'cream' },
    { id: 5, title: bi('Latte Art', 'Latte Art'),          image: '', tone: 'latte' },
    { id: 6, title: bi('Brunch', 'Brunch'),                image: '', tone: 'rose' },
    { id: 7, title: bi('Signature', 'Signature'),          image: '', tone: 'cocoa' },
    { id: 8, title: bi('Fıstık', 'Pistachio'),             image: '', tone: 'pistachio' },
  ],

  instagram: {
    handle: '@glammcoffee',
    url: 'https://www.instagram.com/glammcoffee/',
    followers: '',
    bio: bi('Specialty coffee & handcrafted bakery · Adana 🇹🇷',
            'Specialty coffee & handcrafted bakery · Adana 🇹🇷'),
    // Düzenle: admin panelinden kendi gerçek gönderilerini ekleyebilirsin.
    // Her post için image (upload veya URL) + postUrl (Instagram gönderi linki).
    posts: [
      { id: 1, tone: 'rose',      image: '', postUrl: 'https://www.instagram.com/glammcoffee/', caption: bi('Rose Latte · yeni sezon 🌹', 'Rose Latte · new season 🌹'),     likes: 0, comments: 0, date: '', tag: 'Rose Latte' },
      { id: 2, tone: 'butter',    image: '', postUrl: 'https://www.instagram.com/glammcoffee/', caption: bi('Fırından taze croissant 🥐', 'Fresh from the oven 🥐'),         likes: 0, comments: 0, date: '', tag: 'Croissant' },
      { id: 3, tone: 'pistachio', image: '', postUrl: 'https://www.instagram.com/glammcoffee/', caption: bi('Pistachio Mocha', 'Pistachio Mocha'),                             likes: 0, comments: 0, date: '', tag: 'Pistachio' },
      { id: 4, tone: 'cocoa',     image: '', postUrl: 'https://www.instagram.com/glammcoffee/', caption: bi('Espresso tonic', 'Espresso tonic'),                               likes: 0, comments: 0, date: '', tag: 'Espresso Tonic' },
      { id: 5, tone: 'cream',     image: '', postUrl: 'https://www.instagram.com/glammcoffee/', caption: bi('Basque cheesecake', 'Basque cheesecake'),                         likes: 0, comments: 0, date: '', tag: 'Basque' },
      { id: 6, tone: 'latte',     image: '', postUrl: 'https://www.instagram.com/glammcoffee/', caption: bi('Latte art · Çukurova', 'Latte art · Çukurova'),                   likes: 0, comments: 0, date: '', tag: 'Workshop' },
    ],
  },

  about: {
    kicker: bi('— Hikaye', '— Story'),
    title: bi("2021'de küçük bir tezgahta başladı.", 'It started at a little counter in 2021.'),
    body: bi('Glamm, bir tezgah, birkaç bardak ve bir espresso makinesiyle başladı. Bugün üç şube ve binlerce küçük sabah ritüeli. Değişmeyen tek şey: her kahvenin, her hamurun aynı özenle hazırlanması.',
             'Glamm started with a counter, a few cups and one espresso machine. Today: three stores and thousands of small morning rituals. The one constant: every coffee, every dough, crafted with the same care.'),
    stats: [
      [bi('2021','2021'), bi('İlk şube','First store')],
      [bi('3','3'),       bi('Şube','Stores')],
      [bi('%100','100%'), bi('Specialty grade','Specialty grade')],
    ],
  },

  contact: {
    kicker: bi('— İletişim', '— Contact'),
    title: bi('Bir şey mi soracaktın?', 'Got a question?'),
    body: bi('Rezervasyon, özel sipariş, işbirliği veya sadece bir selam. Cevap genelde aynı gün.',
             'Booking, special orders, partnerships, or just a hello. We usually reply same day.'),
  },

  visibility: {
    hero:       true,
    experience: true,
    featured:   true,
    campaigns:  true,
    branches:   true,
    instagram:  true,
    about:      true,
    contact:    true,
    footer:     true,
  },

  legal: {
    updated: '2026-04-21',
    privacy: {
      title: bi('Gizlilik Politikası', 'Privacy Policy'),
      body: bi(
`Glamm Coffee & Bakery olarak, müşterilerimizin kişisel verilerinin korunması bizim için bir kahve molası kadar önemli. Bu sayfa, web sitemizi kullanırken hangi bilgilerin toplandığını, nasıl saklandığını ve hangi amaçlarla kullanıldığını şeffaf bir dille özetler.

## 1. Topladığımız bilgiler
• İletişim formu aracılığıyla paylaştığın ad-soyad, e-posta, telefon ve mesaj içeriği.
• Ziyaret sırasında sunucu günlüklerine kaydedilen IP adresi, tarayıcı türü ve ziyaret zamanı.
• Bülten onayın olması hâlinde e-posta adresin ve tercih bilgilerin.

## 2. Kullanım amaçları
Verilerinizi yalnızca şu amaçlarla işleriz: taleplerinize cevap vermek, rezervasyon ve sipariş koordinasyonu, yasal yükümlülüklerimizi yerine getirmek ve — sadece açık rızan hâlinde — bülten ve kampanya iletişimi.

## 3. Paylaşım
Verilerinizi üçüncü taraflarla ticari amaçla paylaşmıyoruz. Yalnızca yasal zorunluluk hâlinde yetkili kamu kurumlarına veya hizmet aldığımız güvenilir iş ortaklarımıza (barındırma, e-posta altyapısı) aktarılır.

## 4. Saklama süresi
Veriler, toplama amacının gerektirdiği süre ve yasal saklama yükümlülükleri çerçevesinde muhafaza edilir; süre sonunda güvenli biçimde silinir ya da anonim hâle getirilir.

## 5. Haklarınız
KVKK 11. madde kapsamındaki tüm haklarınızı kullanmak için bize hello@glammcoffee.com adresinden ulaşabilirsiniz. Detay için KVKK Aydınlatma Metnimize bakabilirsin.`,
`At Glamm Coffee & Bakery, protecting our guests' personal data is as important to us as a properly pulled shot. This page explains, in plain language, which information we collect when you use our website, how we store it, and why.

## 1. What we collect
• Name, email, phone and message you share through the contact form.
• Technical logs (IP address, browser type, timestamp) recorded when you visit.
• Your email and preferences if you opt in to our newsletter.

## 2. Why we use it
We process your data only to reply to your requests, coordinate bookings and orders, meet legal obligations, and — only with your explicit consent — send you newsletters and campaign updates.

## 3. Sharing
We do not sell your data or share it for commercial purposes. It is disclosed only to vetted service providers (hosting, email infrastructure) or when required by law.

## 4. Retention
Data is kept only for as long as the purpose requires and as long as legal duties mandate. Afterwards it is securely deleted or anonymised.

## 5. Your rights
Contact hello@glammcoffee.com to exercise any of your rights under Turkish Data Protection Law (KVKK, Art. 11): access, rectification, deletion, and objection. See our KVKK notice for details.`
      ),
    },
    kvkk: {
      title: bi('KVKK Aydınlatma Metni', 'KVKK Data Protection Notice'),
      body: bi(
`6698 Sayılı Kişisel Verilerin Korunması Kanunu ("KVKK") uyarınca veri sorumlusu sıfatıyla Glamm Coffee & Bakery, kişisel verilerinizi aşağıda açıklanan esaslar çerçevesinde işler.

## Veri sorumlusu
Glamm Coffee & Bakery — Adana / Türkiye
İletişim: hello@glammcoffee.com · +90 322 000 00 01

## 1. İşlenen veri kategorileri
• Kimlik: ad-soyad.
• İletişim: e-posta, telefon numarası.
• İşlem güvenliği: IP adresi, log kayıtları.
• Pazarlama: tercih ve onay bilgileri.

## 2. İşleme amaçları
Mal/hizmet sunumu, müşteri ilişkileri yönetimi, sözleşmenin kurulması ve ifası, yasal yükümlülüklerin yerine getirilmesi, talep-şikayet takibi ve açık rızaya dayalı pazarlama iletişimi.

## 3. Hukuki sebepler
KVKK m. 5/2: sözleşmenin kurulması/ifası, meşru menfaat, yasal yükümlülük.
KVKK m. 5/1: açık rıza (pazarlama iletişimi, çerez tercihleri).

## 4. Aktarım
Verileriniz yalnızca gerekli ölçüde, barındırma ve e-posta altyapı sağlayıcılarımıza aktarılır. Yurt dışına aktarım, KVKK m. 9 gereğince açık rızanız alınarak yapılır.

## 5. İlgili kişi hakları (m. 11)
• Kişisel verinin işlenip işlenmediğini öğrenme.
• İşlenmişse bilgi talep etme.
• Amacına uygun kullanılıp kullanılmadığını öğrenme.
• Yurt içinde/yurt dışında aktarıldığı üçüncü kişileri bilme.
• Eksik/yanlış işlenmişse düzeltilmesini isteme.
• Silinmesini veya yok edilmesini isteme.
• Düzeltme/silme/yok etme işlemlerinin üçüncü kişilere bildirilmesini isteme.
• Otomatik sistemlerle çıkan sonuca itiraz etme.
• Kanuna aykırı işleme sebebiyle zararın giderilmesini talep etme.

## 6. Başvuru yolu
Yukarıdaki haklarınız için hello@glammcoffee.com adresine kimlik bilgilerinizi içeren bir e-posta gönderebilir ya da işyerimize yazılı başvuruda bulunabilirsiniz. Talebiniz en geç 30 gün içinde sonuçlandırılır.`,
`Pursuant to Turkish Personal Data Protection Law No. 6698 ("KVKK"), Glamm Coffee & Bakery, acting as data controller, processes your personal data as set out below.

## Data controller
Glamm Coffee & Bakery — Adana, Türkiye
Contact: hello@glammcoffee.com · +90 322 000 00 01

## 1. Categories processed
• Identity: full name.
• Contact: email, phone.
• Transaction security: IP address, server logs.
• Marketing: preferences and consents.

## 2. Purposes
Providing products/services, managing customer relations, entering into and performing contracts, meeting legal duties, handling requests and complaints, and marketing communications based on your explicit consent.

## 3. Legal basis
KVKK Art. 5/2: performance of a contract, legitimate interest, legal obligation.
KVKK Art. 5/1: explicit consent (marketing, cookie preferences).

## 4. Transfers
Your data is transferred, only to the extent necessary, to our hosting and email infrastructure providers. Cross-border transfer is made only with your explicit consent under Art. 9.

## 5. Your rights (Art. 11)
• Learn whether your data is processed.
• Request information if it is.
• Verify lawful use.
• Know third parties to whom it was transferred.
• Request correction if incomplete or inaccurate.
• Request erasure or destruction.
• Be notified of completion to third parties.
• Object to outputs of automated systems.
• Claim damages for unlawful processing.

## 6. How to apply
Email hello@glammcoffee.com with your identifying details, or apply in writing at our premises. We respond within 30 days.`
      ),
    },
    cookies: {
      title: bi('Çerez Politikası', 'Cookie Policy'),
      body: bi(
`Web sitemiz, deneyimini iyileştirmek için çerez adı verilen küçük metin dosyalarını kullanır. Bu sayfa hangi çerezlerin, neden kullanıldığını özetler.

## 1. Zorunlu çerezler
Dil tercihi, karanlık mod, sepet içeriği gibi temel işlevler için gereklidir. Bu çerezler kapatılamaz — site onlar olmadan çalışmaz.

## 2. Performans / analitik
Site trafiğini anonim olarak ölçer, hangi sayfaların popüler olduğunu anlamamıza yardımcı olur. Yalnızca açık rızanla etkinleşir.

## 3. Pazarlama
Kampanya ve sosyal medya reklamları için üçüncü taraf hizmetleri (Instagram, Meta) kullanabiliriz. Yalnızca onaylaman hâlinde devreye girer.

## 4. Çerezleri yönetmek
Tarayıcı ayarlarından çerezleri istediğin zaman silebilir veya engelleyebilirsin. Bazı çerezleri kapatman, sitenin bölümlerinin beklenen şekilde çalışmamasına yol açabilir.

## 5. Saklama süresi
Oturum çerezleri tarayıcı kapandığında silinir. Kalıcı çerezler en fazla 12 ay saklanır, ardından otomatik olarak sona erer.`,
`Our website uses small text files called cookies to improve your experience. This page summarises which cookies we use and why.

## 1. Strictly necessary
Required for basic functionality — language preference, dark mode, cart. These cannot be disabled; the site cannot work without them.

## 2. Performance / analytics
Measures site traffic anonymously and helps us learn which pages resonate. Active only with your explicit consent.

## 3. Marketing
Third-party services (Instagram, Meta) may be used for campaigns and social ads. Runs only when you opt in.

## 4. Managing cookies
You can delete or block cookies from your browser settings at any time. Disabling some cookies may cause parts of the site to behave unexpectedly.

## 5. Retention
Session cookies are cleared when the browser closes. Persistent cookies last up to 12 months, then expire automatically.`
      ),
    },
  },

  footer: {
    blurb: bi('Glamm, günlük bir kahve molasını küçük bir ritüele dönüştürür.',
              'Glamm turns a daily coffee break into a small ritual.'),
    cols: [
      { title: bi('Keşfet', 'Explore'), items: [
        { label: bi('Menü','Menu'),             link: '#/tam-menu' },
        { label: bi('Şubeler','Stores'),        link: '#stores' },
        { label: bi('Kampanyalar','Campaigns'), link: '#campaigns' },
      ]},
      { title: bi('Marka',  'Brand'), items: [
        { label: bi('Hikayemiz','Our Story'), link: '#about' },
      ]},
    ],
    newsletter: { title: bi('Bülten','Newsletter'), sub: bi('Yeni sezon ve küçük sürprizler için.','For new seasons and small surprises.') },
    socials: ['IG','TT','YT','SP'],
    copyright: bi('© {year} Glamm Coffee & Bakery. Tüm hakları saklıdır.',
                  '© {year} Glamm Coffee & Bakery. All rights reserved.'),
  },
};

/* ======================= Store ======================= */

export function deepMerge(a, b) {
  if (Array.isArray(b)) return b.slice();
  if (b && typeof b === 'object' && !('tr' in b && 'en' in b && Object.keys(b).length === 2)) {
    // merge normal objects, but treat bilingual {tr,en} objects as leaf values
    const out = { ...(a || {}) };
    for (const k of Object.keys(b)) out[k] = deepMerge(a?.[k], b[k]);
    return out;
  }
  return b === undefined ? a : b;
}

// Local cache — used as initial boot state AND offline fallback.
function loadLocal() {
  if (typeof localStorage === 'undefined') return { ...DEFAULT_CONTENT };
  try {
    const raw = localStorage.getItem(CONTENT_KEY);
    if (!raw) return { ...DEFAULT_CONTENT };
    const saved = JSON.parse(raw);
    return deepMerge(DEFAULT_CONTENT, saved);
  } catch {
    return { ...DEFAULT_CONTENT };
  }
}

function saveLocal(c) {
  try { localStorage.setItem(CONTENT_KEY, JSON.stringify(c)); } catch {}
}

export const ContentStore = (() => {
  // Boot: local cache first (fast first paint), then hydrate from API.
  let state = loadLocal();
  let remoteOk = false;
  const subs = new Set();
  const notify = () => subs.forEach(fn => fn(state));

  const commit = (next) => {
    state = next;
    saveLocal(state);
    notify();
  };

  // Hydrate from API as soon as possible.
  (async () => {
    try {
      const res = await api.getContent();
      if (res && res.content) {
        remoteOk = true;
        commit(deepMerge(DEFAULT_CONTENT, res.content));
      } else {
        remoteOk = true;
      }
    } catch {
      // API unreachable — stay on local cache / defaults.
      remoteOk = false;
    }
  })();

  // Persist to backend (admin only — backend enforces auth).
  async function pushRemote(next) {
    try {
      await api.putContent(next);
      remoteOk = true;
      return true;
    } catch (e) {
      console.warn('[content] push failed', e);
      return false;
    }
  }

  return {
    get: () => state,
    isRemoteOk: () => remoteOk,

    // Local-only updates (draft editing — admin panel handles "save").
    set:     (next)  => commit(next),
    update:  (patch) => commit(deepMerge(state, patch)),
    replace: (fresh) => commit(fresh),
    reset:   ()      => commit(JSON.parse(JSON.stringify(DEFAULT_CONTENT))),

    // Persist to server (admin panel uses this after login).
    publish: async (next) => {
      const target = next || state;
      const ok = await pushRemote(target);
      if (ok && next) commit(next);
      return ok;
    },

    subscribe: (fn) => { subs.add(fn); return () => subs.delete(fn); },
    defaults: () => JSON.parse(JSON.stringify(DEFAULT_CONTENT)),
  };
})();

export function useContent() {
  const [state, setState] = useState(ContentStore.get());
  useEffect(() => ContentStore.subscribe(setState), []);
  return state;
}

/* ======================= Tone palette ======================= */

export const TONE_BG = {
  rose:      'linear-gradient(135deg, #f5dcd4 0%, #e9b7a6 100%)',
  cream:     'linear-gradient(135deg, #faf5ee 0%, #ede0ce 100%)',
  latte:     'linear-gradient(135deg, #ede0ce 0%, #c9a575 100%)',
  cocoa:     'linear-gradient(135deg, #8b5a3c 0%, #4a2e1e 100%)',
  butter:    'linear-gradient(135deg, #fff1d6 0%, #e4c79a 100%)',
  pistachio: 'linear-gradient(135deg, #d9dfb6 0%, #a6b38a 100%)',
  white:     'linear-gradient(135deg, #ffffff 0%, #f5ebdb 100%)',
};
